import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateAppointmentStatus } from '../services/appointmentService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

const PatientCheckInSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const appointment = location.state?.appointment;

  useEffect(() => {
    if (!appointment) {
      setError('No appointment data found');
      setIsProcessing(false);
      return;
    }

    // Update appointment status to checked-in (this will trigger encounter creation)
    const performCheckIn = async () => {
      try {
        await updateAppointmentStatus(appointment.id, 'checked-in');
        setSuccess(true);
        setIsProcessing(false);
      } catch (error) {
        console.error('Error during check-in:', error);
        setError('Failed to complete check-in. Please see the front desk.');
        setIsProcessing(false);
      }
    };

    performCheckIn();
  }, [appointment]);

  const handleDone = () => {
    navigate('/');
  };

  if (!appointment) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        p: 3
      }}>
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              No appointment data found
            </Alert>
            <Button variant="contained" onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      p: 3
    }}>
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          {isProcessing ? (
            <>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Processing Your Check-In
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we complete your check-in and prepare your encounter...
              </Typography>
            </>
          ) : error ? (
            <>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Button variant="contained" onClick={() => navigate('/')}>
                Return to Home
              </Button>
            </>
          ) : (
            <>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
              <Typography variant="h4" gutterBottom color="success.main">
                Check-In Complete!
              </Typography>
              <Typography variant="h6" gutterBottom>
                Welcome, {appointment.patientName}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your appointment has been successfully checked in and your encounter has been created.
                Please have a seat and someone will call you shortly.
              </Typography>
              
              <Box sx={{ textAlign: 'left', mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Appointment Details:
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {new Date(appointment.date || appointment.scheduledTime).toLocaleDateString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong> {new Date(appointment.date || appointment.scheduledTime).toLocaleTimeString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Provider:</strong> {appointment.doctorName || appointment.provider}
                </Typography>
                <Typography variant="body2">
                  <strong>Purpose:</strong> {appointment.purpose || appointment.reason}
                </Typography>
              </Box>
              
              <Button 
                variant="contained" 
                size="large"
                onClick={handleDone}
                sx={{ px: 4 }}
              >
                Done
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PatientCheckInSuccess; 